import { call, put, select, takeEvery, takeLatest } from "redux-saga/effects";

import { MODAL_TYPES, NameOfRoutes } from "@shared/constants";
import {
  CASE_DOCUMENT_STATUS,
  CASE_FILE_KIND,
  CASE_STATUS,
  CaseDocumentModel,
  CaseModel,
  CaseStatusModel,
  MedicalFieldResultModel,
} from "@shared/models";
import { navigate, showNotification, startLoading, stopLoading } from "@shared/store/actions";
import { selectors as sharedSelectors } from "@shared/store";
import {
  ActionWithPayload,
  ModalProps,
  PaginatedResponse,
  BaseFilter,
  CreateEditResponse,
  ResponseError,
  WithCallback,
  WithLoaderFlag,
} from "@shared/interfaces";

import { CasesReportStatusDtoInterface } from "../interface/CasesReportStatusDto.interface";
import api from "../api";
import {
  chooseMedicalFieldValue,
  chooseMedicalFieldValueSuccess,
  verifyMedicalField,
  verifyMedicalFieldSuccess,
  createCase,
  deleteCase,
  deleteCaseFile,
  discardTemporaryCaseFiles,
  getCase,
  getCaseData,
  getCaseDocuments,
  getCaseFileSignedUrl,
  getCaseFileViewUrl,
  getCasesList,
  getCasesStatuses,
  getMedicalRecordsResult,
  parseCaseDocument,
  payForCase,
  removeCaseDocument,
  removeCaseDocumentSuccess,
  restartProcessingCase,
  retryCaseDocument,
  syncCaseDocumentFields,
  updateCase,
  updateCaseReportStatus,
  uploadCaseFile,
} from "./actions";
import { getCaseDocuments as selectCaseDocuments } from "./selectors";
import {
  CaseDocumentDto,
  CaseFileDto,
  CheckedCaseFile,
  ChooseMedicalFieldValuePayload,
  VerifyMedicalFieldPayload,
  CreateCaseDto,
  ParseCaseDocumentDto,
  RemoveCaseDocumentPayload,
  TemporaryCaseFileUpload,
  UploadCaseFileDto,
} from "../interface";
import { CASE_DOCUMENT_INFO } from "../constants/caseDocuments";
import { getMedicalSummariesParsedMessage, hasParsingFailed, UPLOAD_FAILED_MESSAGE } from "../utils/caseDocuments";

function* getCasesListSaga({ payload }: ActionWithPayload<WithLoaderFlag<BaseFilter>>) {
  const { isLoadingBlocked, ...restPayload } = payload;
  try {
    if (!isLoadingBlocked) yield put(startLoading());
    const response: PaginatedResponse<CaseModel> = yield call(api.getCasesList, restPayload);
    yield put(
      getCasesList.success({
        ...response,
        clear: !payload.page,
      }),
    );
  } catch (error) {
    yield put(getCasesList.failure(error as Error));
  } finally {
    if (!isLoadingBlocked) yield put(stopLoading());
  }
}

function* getCasesStatusesSaga({ payload }: ActionWithPayload<number[]>) {
  try {
    const response: { rows: CaseStatusModel[] } = yield call(api.getCasesStatuses, payload);
    yield put(getCasesStatuses.success(response.rows));
  } catch (error) {
    yield put(getCasesStatuses.failure(error as Error));
  }
}

function* createCaseSaga({ payload }: ActionWithPayload<WithCallback<CreateCaseDto, CaseModel>>) {
  const { cb, ...rest } = payload;
  try {
    yield put(startLoading());
    const response: CreateEditResponse<CaseModel> = yield call(api.createCase, rest);
    if (response.element.status !== CASE_STATUS.INVALID && response.element.status !== CASE_STATUS.FAILURE) {
      yield put(createCase.success(response));
    }
    cb?.(response.element);
  } catch (error) {
    yield put(createCase.failure(error as Error));
  } finally {
    yield put(stopLoading());
  }
}

function* deleteCaseSaga({ payload }: ActionWithPayload<WithCallback<{ caseId: number }, undefined>>) {
  const { cb, caseId } = payload;
  try {
    yield put(startLoading());
    yield call(api.deleteCase, caseId);
    cb?.();
  } catch (error) {
    yield put(getCasesList.failure(error as Error));
  } finally {
    yield put(stopLoading());
  }
}

function* updateCaseSaga({ payload }: ActionWithPayload<WithCallback<CaseModel, CaseModel>>) {
  try {
    yield put(startLoading());
    const { id, cb, ...rest } = payload;
    const response: CreateEditResponse<CaseModel> = yield call(api.updateCase, id, rest);
    yield put(updateCase.success(response));
    cb?.(response.element);
  } catch (error) {
    yield put(updateCase.failure(error as Error));
  } finally {
    yield put(stopLoading());
  }
}

function* getCaseSaga({ payload }: ActionWithPayload<WithLoaderFlag<{ caseId: number }>>) {
  try {
    if (!payload.isLoadingBlocked) yield put(startLoading());
    const response: CaseModel = yield call(api.getCase, payload.caseId);
    yield put(getCase.success(response));
  } catch (error) {
    yield put(getCase.failure(error as Error));
    yield put(navigate(NameOfRoutes.CASES_LIST));
  } finally {
    if (!payload.isLoadingBlocked) yield put(stopLoading());
  }
}

function* getCaseDataSaga({ payload }: ActionWithPayload<WithCallback<{ caseId: number }, CaseModel>>) {
  try {
    const { caseId, cb } = payload;
    yield put(startLoading());
    const response: CaseModel = yield call(api.getCase, caseId);
    cb?.(response);
    yield put(getCaseData.success(response));
  } catch (error) {
    yield put(getCaseData.failure(error as Error));
  } finally {
    yield put(stopLoading());
  }
}

function* restartProcessingCaseSaga({ payload }: ActionWithPayload<number>) {
  try {
    yield put(startLoading());
    const response: CreateEditResponse<CaseModel> = yield call(api.restartProcessingCase, payload);
    yield put(restartProcessingCase.success(response));
  } catch (error) {
    yield put(restartProcessingCase.failure(error as Error));
  } finally {
    yield put(stopLoading());
  }
}

function* updateCaseReportStatusSaga({
  payload,
}: ActionWithPayload<WithCallback<CasesReportStatusDtoInterface, CasesReportStatusDtoInterface>>) {
  const { cb, id, ...rest } = payload;
  try {
    yield put(startLoading());
    const response: CreateEditResponse<CaseModel> = yield call(api.updateCaseReportStatus, id, rest);
    yield put(updateCaseReportStatus.success(response.element));
    cb?.(response.element);
  } catch (error) {
    yield put(updateCaseReportStatus.failure(error as Error));
  } finally {
    yield put(stopLoading());
  }
}

function* getCaseFileSignedUrlSaga({ payload }: ActionWithPayload<number>) {
  try {
    yield put(startLoading());
    const response: { url: string } = yield call(api.getCaseFileSignedUrl, payload);
    window.open(response.url, "_blank");
  } catch (error) {
    yield put(getCaseFileSignedUrl.failure(error as Error));
  } finally {
    yield put(stopLoading());
  }
}

function* getCaseFileViewUrlSaga({ payload }: ActionWithPayload<WithCallback<CaseFileDto, string>>) {
  const { cb, ...dto } = payload;
  try {
    yield put(startLoading());
    const response: { url: string } = yield call(api.getCaseFileViewUrl, dto);
    cb?.(response.url);
  } catch (error) {
    cb?.();
    yield put(getCaseFileViewUrl.failure(error as Error));
  } finally {
    yield put(stopLoading());
  }
}

const isParsing = (caseDocuments: CaseDocumentModel[], kind: CASE_FILE_KIND) =>
  caseDocuments.some(
    (caseDocument) => caseDocument.kind === kind && caseDocument.status === CASE_DOCUMENT_STATUS.PARSING,
  );

function* notifyMedicalSummariesParsed(caseObj: CaseModel, parsingDocument: CaseDocumentModel) {
  const medicalSummaries = caseObj.case_documents?.find(({ kind }) => kind === CASE_FILE_KIND.MEDICAL);

  if (!medicalSummaries) return;

  if (hasParsingFailed(medicalSummaries, parsingDocument.files)) {
    yield put(
      showNotification({
        message: "Medical summaries couldn't be parsed. Retry or replace the files.",
        appearance: "error",
      }),
    );
    return;
  }

  const modal: ModalProps | undefined = yield select(sharedSelectors.getModal());

  // The open upload window shows the result itself.
  if (modal?.type !== MODAL_TYPES.UPLOAD_MEDICAL_RECORDS) {
    yield put(showNotification({ message: getMedicalSummariesParsedMessage(medicalSummaries), appearance: "success" }));
  }
}

function* notifyCaseNotesParsed(caseObj: CaseModel, parsingDocument: CaseDocumentModel) {
  const caseNotes = caseObj.case_documents?.find(({ kind }) => kind === CASE_FILE_KIND.NOTES);

  if (!caseNotes) return;

  yield put(
    hasParsingFailed(caseNotes, parsingDocument.files)
      ? showNotification({ message: "Case notes couldn't be parsed. Replace or remove the file.", appearance: "error" })
      : showNotification({ message: "Case notes saved to section 07", appearance: "success" }),
  );
}

function* getCaseDocumentsSaga({ payload }: ActionWithPayload<number>) {
  try {
    const previousDocuments: CaseDocumentModel[] = yield select(selectCaseDocuments());
    const response: CaseModel = yield call(api.getCase, payload);
    const caseDocuments = response.case_documents ?? [];

    yield put(getCaseDocuments.success(caseDocuments));

    const parsedDocuments = previousDocuments.filter(
      ({ kind, status }) =>
        kind !== CASE_FILE_KIND.PSR && status === CASE_DOCUMENT_STATUS.PARSING && !isParsing(caseDocuments, kind),
    );

    if (parsedDocuments.length) {
      yield put(syncCaseDocumentFields(response));
    }
    for (const parsingDocument of parsedDocuments) {
      yield call(
        parsingDocument.kind === CASE_FILE_KIND.NOTES ? notifyCaseNotesParsed : notifyMedicalSummariesParsed,
        response,
        parsingDocument,
      );
    }
  } catch (error) {
    yield put(getCaseDocuments.failure(error as Error));
  }
}

function* uploadCaseFileSaga({ payload }: ActionWithPayload<WithCallback<UploadCaseFileDto, CheckedCaseFile>>) {
  const { cb, ...dto } = payload;

  try {
    const upload: TemporaryCaseFileUpload = yield call(api.createCaseFile, dto);

    try {
      yield call(api.uploadFileToStorage, upload.upload_url, dto.file, upload.mime_type);
    } catch {
      // The check below finds the file missing, says the upload failed and deletes the temporary file.
    }

    const checkedFile: CheckedCaseFile = yield call(api.confirmCaseFile, {
      caseId: dto.caseId,
      fileId: upload.id,
      replace: dto.replace,
    });
    cb?.(checkedFile);
  } catch (error) {
    const { code, message } = error as ResponseError;
    // Only the API's own errors are worded for users; a network failure isn't.
    cb?.({ id: null, pages: null, error: (code && message) || UPLOAD_FAILED_MESSAGE, can_retry: true });
  }
}

function* deleteCaseFileSaga({ payload }: ActionWithPayload<WithCallback<CaseFileDto>>) {
  const { cb, ...dto } = payload;
  try {
    yield call(api.deleteCaseFile, dto);
    yield put(deleteCaseFile.success());
    cb?.();
  } catch (error) {
    yield put(deleteCaseFile.failure(error as Error));
  }
}

function* discardTemporaryCaseFilesSaga({ payload }: ActionWithPayload<CaseDocumentDto>) {
  try {
    yield call(api.discardTemporaryCaseFiles, payload);
  } catch {
    // Not worth a message: the server deletes temporary files after a day anyway.
  }
}

function* parseCaseDocumentSaga({ payload }: ActionWithPayload<WithCallback<ParseCaseDocumentDto>>) {
  const { cb, ...dto } = payload;
  try {
    const response: { case_documents: CaseDocumentModel[] } = yield call(api.parseCaseDocument, dto);
    yield put(parseCaseDocument.success(response.case_documents));
    cb?.();
  } catch (error) {
    yield put(parseCaseDocument.failure(error as Error));
  }
}

function* retryCaseDocumentSaga({ payload }: ActionWithPayload<CaseDocumentDto>) {
  try {
    yield put(startLoading());
    const response: { case_documents: CaseDocumentModel[] } = yield call(api.retryCaseDocument, payload);
    yield put(retryCaseDocument.success(response.case_documents));
  } catch (error) {
    yield put(retryCaseDocument.failure(error as Error));
  } finally {
    yield put(stopLoading());
  }
}

function* removeCaseDocumentSaga({ payload }: ActionWithPayload<RemoveCaseDocumentPayload>) {
  const { cb, onError, ...dto } = payload;
  try {
    const caseObj: CaseModel = yield call(api.removeCaseDocument, dto);
    yield put(removeCaseDocumentSuccess(caseObj));
    yield put(syncCaseDocumentFields(caseObj));
    yield put(showNotification({ message: `${CASE_DOCUMENT_INFO[dto.kind].name} removed`, appearance: "success" }));
    cb?.();
  } catch (error) {
    const { code, message } = error as ResponseError;
    onError((code && message) || "Something went wrong. Please try again.");
  }
}

function* chooseMedicalFieldValueSaga({ payload }: ActionWithPayload<ChooseMedicalFieldValuePayload>) {
  const { cb, onError, ...dto } = payload;
  try {
    const caseObj: CaseModel = yield call(api.chooseMedicalFieldValue, dto);
    yield put(chooseMedicalFieldValueSuccess(caseObj));
    yield put(syncCaseDocumentFields(caseObj));
    cb?.();
  } catch (error) {
    const { code, message } = error as ResponseError;
    onError((code && message) || "Something went wrong. Please try again.");
  }
}

function* verifyMedicalFieldSaga({ payload }: ActionWithPayload<VerifyMedicalFieldPayload>) {
  const { cb, onError, ...dto } = payload;
  try {
    const caseObj: CaseModel = yield call(api.verifyMedicalField, dto);
    yield put(verifyMedicalFieldSuccess(caseObj));
    yield put(syncCaseDocumentFields(caseObj));
    cb?.();
  } catch (error) {
    const { code, message } = error as ResponseError;
    onError((code && message) || "Something went wrong. Please try again.");
  }
}

function* getMedicalRecordsResultSaga({ payload }: ActionWithPayload<number>) {
  try {
    const { fields }: { fields: MedicalFieldResultModel[] } = yield call(api.getMedicalRecordsResult, payload);
    yield put(getMedicalRecordsResult.success(fields));
  } catch (error) {
    yield put(getMedicalRecordsResult.failure(error as Error));
  }
}

function* payForCaseSaga({ payload }: ActionWithPayload<number>) {
  try {
    yield put(startLoading());
    yield call(api.payForCase, payload);
    yield put(getCase.request({ caseId: payload }));
  } catch (error) {
    yield put(getCase.failure(error as Error));
  } finally {
    yield put(stopLoading());
  }
}

function* casesSaga() {
  yield takeLatest(getCasesList.request, getCasesListSaga);
  yield takeLatest(getCasesStatuses.request, getCasesStatusesSaga);
  yield takeLatest(createCase.request, createCaseSaga);
  yield takeLatest(getCase.request, getCaseSaga);
  yield takeLatest(restartProcessingCase.request, restartProcessingCaseSaga);
  yield takeLatest(updateCaseReportStatus.request, updateCaseReportStatusSaga);
  yield takeLatest(getCaseFileSignedUrl.request, getCaseFileSignedUrlSaga);
  yield takeEvery(getCaseFileViewUrl.request, getCaseFileViewUrlSaga);
  yield takeLatest(getCaseDocuments.request, getCaseDocumentsSaga);
  yield takeEvery(uploadCaseFile, uploadCaseFileSaga);
  yield takeEvery(deleteCaseFile.request, deleteCaseFileSaga);
  yield takeEvery(discardTemporaryCaseFiles, discardTemporaryCaseFilesSaga);
  yield takeLatest(parseCaseDocument.request, parseCaseDocumentSaga);
  yield takeLatest(retryCaseDocument.request, retryCaseDocumentSaga);
  yield takeLatest(removeCaseDocument, removeCaseDocumentSaga);
  yield takeLatest(chooseMedicalFieldValue, chooseMedicalFieldValueSaga);
  yield takeLatest(verifyMedicalField, verifyMedicalFieldSaga);
  yield takeLatest(getMedicalRecordsResult.request, getMedicalRecordsResultSaga);
  yield takeLatest(payForCase.request, payForCaseSaga);
  yield takeLatest(deleteCase.request, deleteCaseSaga);
  yield takeLatest(updateCase.request, updateCaseSaga);
  yield takeLatest(getCaseData.request, getCaseDataSaga);
}

export default casesSaga;
